import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

import {
  getResourceFileMaxSize,
  RESOURCE_CONTENT_TYPE,
  RESOURCE_CONTENT_TYPE_PROPS,
} from 'src/data/performance';

export const UploadResourceFileHelper = forwardRef(({ allowIncreasedFileSize }, ref) => {
  const promiseResolveRef = useRef(null);
  const promiseRejectRef = useRef(null);

  const handleDrop = useCallback((acceptedFiles, fileRejections) => {
    if (!promiseResolveRef.current) {
      return;
    }

    promiseResolveRef.current({ acceptedFiles, fileRejections });
  }, []);

  const handleFileDialogCancel = useCallback(() => {
    if (!promiseResolveRef.current) {
      return;
    }

    promiseResolveRef.current(null);
  }, []);

  const handleError = useCallback((error) => {
    if (!promiseRejectRef.current) {
      return;
    }

    promiseRejectRef.current(error);
  }, []);

  const {
    getRootProps: getDocumentRootProps,
    getInputProps: getDocumentInputProps,
    open: openDocumentDropzone,
  } = useDropzone({
    noKeyboard: true,
    noClick: true,
    accept: RESOURCE_CONTENT_TYPE_PROPS[RESOURCE_CONTENT_TYPE.document]?.acceptedFileType,
    onDrop: handleDrop,
    onFileDialogCancel: handleFileDialogCancel,
    onError: handleError,
    maxFiles: 1,
    multiple: false,
    maxSize: getResourceFileMaxSize({
      resourceType: RESOURCE_CONTENT_TYPE.document,
      allowIncreasedFileSize,
    }),
  });

  const {
    getRootProps: getImageRootProps,
    getInputProps: getImageInputProps,
    open: openImageDropzone,
  } = useDropzone({
    noKeyboard: true,
    noClick: true,
    accept: RESOURCE_CONTENT_TYPE_PROPS[RESOURCE_CONTENT_TYPE.image]?.acceptedFileType,
    onDrop: handleDrop,
    onFileDialogCancel: handleFileDialogCancel,
    onError: handleError,
    maxFiles: 1,
    multiple: false,
    maxSize: getResourceFileMaxSize({
      resourceType: RESOURCE_CONTENT_TYPE.image,
      allowIncreasedFileSize,
    }),
  });

  const {
    getRootProps: getAudioRootProps,
    getInputProps: getAudioInputProps,
    open: openAudioDropzone,
  } = useDropzone({
    noKeyboard: true,
    noClick: true,
    accept: RESOURCE_CONTENT_TYPE_PROPS[RESOURCE_CONTENT_TYPE.audio]?.acceptedFileType,
    onDrop: handleDrop,
    onFileDialogCancel: handleFileDialogCancel,
    onError: handleError,
    maxFiles: 1,
    multiple: false,
    maxSize: getResourceFileMaxSize({
      resourceType: RESOURCE_CONTENT_TYPE.audio,
      allowIncreasedFileSize,
    }),
  });

  const {
    getRootProps: getVideoRootProps,
    getInputProps: getVideoInputProps,
    open: openVideoDropzone,
  } = useDropzone({
    noKeyboard: true,
    noClick: true,
    accept: RESOURCE_CONTENT_TYPE_PROPS[RESOURCE_CONTENT_TYPE.video]?.acceptedFileType,
    onDrop: handleDrop,
    onFileDialogCancel: handleFileDialogCancel,
    onError: handleError,
    maxFiles: 1,
    multiple: false,
    maxSize: getResourceFileMaxSize({
      resourceType: RESOURCE_CONTENT_TYPE.video,
      allowIncreasedFileSize,
    }),
  });

  const handleOpenFileDialog = useCallback(
    (resourceType) => {
      const promise = new Promise((resolve, reject) => {
        promiseResolveRef.current = resolve;
        promiseRejectRef.current = reject;
      });

      switch (resourceType) {
        case RESOURCE_CONTENT_TYPE.image:
          openImageDropzone();
          break;
        case RESOURCE_CONTENT_TYPE.audio:
          openAudioDropzone();
          break;
        case RESOURCE_CONTENT_TYPE.video:
          openVideoDropzone();
          break;
        case RESOURCE_CONTENT_TYPE.document:
        default:
          openDocumentDropzone();
          break;
      }

      return promise;
    },
    [openImageDropzone, openAudioDropzone, openVideoDropzone, openDocumentDropzone]
  );

  useImperativeHandle(ref, () => ({
    openFileDialog: handleOpenFileDialog,
  }));

  return (
    <>
      <div {...getDocumentRootProps()}>
        <input {...getDocumentInputProps()} />
      </div>

      <div {...getImageRootProps()}>
        <input {...getImageInputProps()} />
      </div>

      <div {...getAudioRootProps()}>
        <input {...getAudioInputProps()} />
      </div>

      <div {...getVideoRootProps()}>
        <input {...getVideoInputProps()} />
      </div>
    </>
  );
});

UploadResourceFileHelper.displayName = 'UploadResourceFileHelper';

UploadResourceFileHelper.propTypes = {
  allowIncreasedFileSize: PropTypes.bool,
};

UploadResourceFileHelper.defaultProps = {
  allowIncreasedFileSize: false,
};
